import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CarFront,
  Disc3,
  Droplets,
  Hammer,
  ShieldCheck,
  SprayCan,
} from "lucide-react";
import { useLanguage } from "../i18n";
import { SERVICE_IMAGES } from "../data/cars";

const LAYOUT = [
  { icon: CarFront, span: "md:col-span-4", image: null, minH: "min-h-[280px]" },
  { icon: Disc3, span: "md:col-span-2", image: "/photos/shop-interior.jpg", minH: "min-h-[280px]" },
  { icon: Droplets, span: "md:col-span-2", image: SERVICE_IMAGES.mechanic, minH: "min-h-[240px]" },
  { icon: Hammer, span: "md:col-span-2", image: null, minH: "min-h-[240px]" },
  { icon: SprayCan, span: "md:col-span-2", image: "/photos/paint-booth.jpg", minH: "min-h-[240px]" },
  { icon: ShieldCheck, span: "md:col-span-6", image: null, minH: "" },
];

const ServicesGrid = () => {
  const { t, lang } = useLanguage();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative border-t border-white/10 bg-[#0D0F14] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.35em] text-[#E63946]">
              {t.services.eyebrow}
            </p>
            <h2 className="font-display mt-3 max-w-xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t.services.title}
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-[#9E9E9E]">{t.services.sub}</p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-6">
          {t.services.items.map((item, i) => {
            const cfg = LAYOUT[i];
            const Icon = cfg.icon;
            const isBanner = i === 5;
            return (
              <motion.div
                key={`${lang}-service-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                data-testid={`service-card-${i}`}
                className={`group relative overflow-hidden border border-white/10 bg-[#12141C] transition-colors duration-300 hover:border-[#E63946]/50 ${cfg.span} ${cfg.minH} ${
                  isBanner ? "md:flex md:items-center md:gap-8 md:py-8" : ""
                }`}
              >
                {cfg.image && (
                  <>
                    <img
                      src={cfg.image}
                      alt={item.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover opacity-25 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-40"
                    />
                    <div className="absolute inset-0 bg-[#0A0B0E]/40" />
                  </>
                )}
                <div className={`relative flex h-full flex-col justify-between p-6 sm:p-7 ${isBanner ? "md:flex-row md:items-center md:gap-8 md:p-0" : ""}`}>
                  <div className={isBanner ? "md:max-w-xl" : ""}>
                    <span className="flex h-11 w-11 items-center justify-center border border-[#E63946]/40 bg-[#E63946]/10 text-[#E63946]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-display mt-5 text-xl font-bold text-white sm:text-2xl">
                      {item.title}
                    </h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-[#9E9E9E]">
                      {item.desc}
                    </p>
                  </div>
                  <span className="mt-6 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 text-[#6C757D] transition-all duration-300 group-hover:border-[#E63946] group-hover:bg-[#E63946] group-hover:text-white md:mt-0">
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
